import type { CalendarEvent } from 'paintscout'
import { removeAuth0Prefix } from '@paintscout/util/users/removeAuth0Prefix'

interface ProjectManagerRef {
  id?: string | null
}

export interface ProjectManagerCrew {
  _id: string
  projectManager?: ProjectManagerRef | null
}

export function getFilteredAndOrderedProjectManagerIds({
  crews,
  selectedProjectManagers,
  projectManagerOrder
}: {
  crews: ProjectManagerCrew[]
  selectedProjectManagers: string[]
  projectManagerOrder: string[]
}): string[] {
  const allProjectManagerIds = Array.from(
    new Set(crews.map((crew) => crew.projectManager?.id).filter((id): id is string => Boolean(id)))
  )

  const normalizedSelectedProjectManagers = new Set(
    selectedProjectManagers.map((projectManagerId) => removeAuth0Prefix(projectManagerId))
  )

  const filteredProjectManagerIds =
    normalizedSelectedProjectManagers.size > 0
      ? allProjectManagerIds.filter((projectManagerId) =>
          normalizedSelectedProjectManagers.has(removeAuth0Prefix(projectManagerId))
        )
      : allProjectManagerIds

  return [
    ...projectManagerOrder.filter((id) =>
      filteredProjectManagerIds.some(
        (projectManagerId) => removeAuth0Prefix(projectManagerId) === removeAuth0Prefix(id)
      )
    ),
    ...filteredProjectManagerIds.filter(
      (projectManagerId) =>
        !projectManagerOrder.some(
          (orderedProjectManagerId) =>
            removeAuth0Prefix(orderedProjectManagerId) === removeAuth0Prefix(projectManagerId)
        )
    )
  ]
}

export function getCrewsForProjectManager(crews: ProjectManagerCrew[], projectManagerId: string): ProjectManagerCrew[] {
  return crews.filter((crew) => {
    const crewProjectManagerId = crew.projectManager?.id
    return (
      Boolean(crewProjectManagerId) && removeAuth0Prefix(crewProjectManagerId) === removeAuth0Prefix(projectManagerId)
    )
  })
}

export function getCrewIdsForProjectManager({
  projectManagerId,
  crews,
  projectManagerEvents,
  crewOrder = []
}: {
  projectManagerId: string
  crews: ProjectManagerCrew[]
  projectManagerEvents: CalendarEvent[]
  crewOrder?: string[]
}): string[] {
  const managersCrews = getCrewsForProjectManager(crews, projectManagerId)
  let crewIds = managersCrews.map((crew) => crew._id)

  const eventsByCrewId = projectManagerEvents.reduce(
    (acc, event) => {
      const crewId = event.crew || 'no-crew'
      if (!acc[crewId]) acc[crewId] = []
      acc[crewId].push(event)
      return acc
    },
    {} as Record<string, CalendarEvent[]>
  )

  const eventCrewIds = Object.keys(eventsByCrewId).sort((a, b) => {
    if (a === 'no-crew') return 1
    if (b === 'no-crew') return -1
    return a.localeCompare(b)
  })

  const additionalCrewIds = eventCrewIds.filter((id) => id !== 'no-crew' && !crewIds.includes(id))
  crewIds = [...crewIds, ...additionalCrewIds]
  if (eventCrewIds.includes('no-crew')) {
    crewIds.push('no-crew')
  }

  if (crewOrder.length > 0) {
    const orderedCrews = crewOrder.filter((id) => crewIds.includes(id))
    const unorderedCrews = crewIds.filter((id) => !crewOrder.includes(id))
    crewIds = [...orderedCrews, ...unorderedCrews]
  }

  return crewIds
}
